/**
 * wheelPropsEqual — the React.memo comparator for <WheelComponent>.
 *
 * Extracted VERBATIM from the inline `React.memo(WheelComponent, …)` comparator
 * (Nick: one behaviour per file + a matching test — this comparator was
 * previously un-unit-testable inline) and then given ONE targeted fix:
 *
 * ── THE FIX (EW-003, flag REACT_APP_SEGMENT_ARC_FILLS) ──────────────────────
 * The per-item comparison only inspected `id / value / color / selected /
 * position`. A **Segment Fill** change (Pattern / Gradient / Image / GIF) updates
 * the segment's fill MODEL (`settings.fill` + the legacy `bgWallpaper /
 * segmentBgColor / currentBg / isPattern` fields) but NOT `color` — so the
 * comparator returned `true` (props "equal") and React.memo SKIPPED the
 * re-render. The wheel WEDGE therefore never re-hydrated its fill on a live edit
 * in the Builder: `hydrateChartFill` (fed to the pie via `hydratedChartData`)
 * never recomputed, so the wedge kept its flat colour until a full page reload
 * remounted the component. (The View-Wheel page mounts fresh, so its wedge WAS
 * correct — this bug was isolated to the live-editing Builder. MEASURED live on
 * web-stg 2026-08-18, bundle main.56e25379.js.)
 *
 * The fix adds a wedge-fill comparison to the per-item loop, using the SAME
 * `resolveSegmentFill` resolver the renderer's `hydrateChartFill` uses — so the
 * memo re-renders exactly when the resolved wedge fill would change, never more.
 *
 * FLAG-GATED / byte-identical when OFF: the extra comparison only runs when
 * `SEGMENT_ARC_FILLS_ENABLED`. With the flag OFF the wedge never paints a fill
 * model anyway (`hydrateChartFill` is a pass-through), so skipping the compare is
 * correct AND keeps the comparator's behaviour identical to before this change.
 *
 * Note on JSON.stringify: the caller's own header warns it is "slow for large
 * objects" for the WHOLE props — that still holds; here we stringify only the
 * small, already-resolved fill MODEL of ONE item (null for a plain-colour
 * segment), and only when the flag is ON. Worst case a differing key order forces
 * one harmless extra redraw; it can never MISS a real change (unequal fills never
 * serialize equal), which is the only failure mode that matters for this bug.
 */

import { resolveSegmentFill } from "../SegmentBuilder/Shared/SegmentStyling/logic/fillModelFeed";
import { SEGMENT_ARC_FILLS_ENABLED } from "../D3Wheels/segmentArcFillsFlag";
import { SEGMENT_WALLPAPER_ENABLED } from "../SegmentView/segmentWallpaperFlag";

// Serialised wedge-fill signature for a chart item (null when the segment models
// no wedge fill — a plain colour). Two items with the same signature paint the
// same wedge fill; a changed signature must force a re-render.
const fillSignature = (item) => {
  const fill = resolveSegmentFill(item);
  return fill ? JSON.stringify(fill) : "";
};

// ISP-9999 (segment-state desync / data loss): per-chart-item fields whose
// change MUST force a WheelComponent re-render, so the D3 pie's internal
// chartData re-syncs through the existing updateArcs(hydratedChartData) effect.
//
// Root cause this list fixes: the pie keeps its OWN internal chartData array
// and, on a wedge-selection click, emits that whole array back to redux
// (setWheelSegments). The pie only re-syncs that array via updateArcs when
// WheelComponent re-renders — but the comparator previously compared ONLY
// id/value/color/selected/position. A segment TITLE edit or a segment
// PHOTO/WALLPAPER delete changes NONE of those, so the component skipped
// rendering, the pie kept a STALE array, and the NEXT wedge click emitted that
// stale array back into redux — silently REVERTING the just-saved edit (title
// dropped, deleted photo reappears). Comparing the content keys below makes any
// content edit re-render, and a single re-render re-syncs the WHOLE hydrated
// item into the pie. Not flag-gated: this loss can happen with EW-003 arc-fills
// OFF, so the content compare must always run (the EW-003 fillSignature compare
// below stays flag-gated and is additive to this).
//
// NOTE: the resolved `icon` on a hydrated item is a React element (a fresh ref
// every render), so it is deliberately NOT compared here — comparing it would
// force a render on every parent update and defeat the memo. The underlying
// scalar content that co-changes with the reported loss vectors is covered.
export const WHEEL_CHART_ITEM_COMPARE_KEYS = [
  "id",
  "value",
  "color",
  "selected",
  "position",
  // Content keys (ISP-9999): label + fill/photo/wallpaper/pattern scalars.
  "title",
  "bgWallpaper",
  "currentBg",
  "patternId",
  "patternColor",
  "segmentBgColor",
  "wallpaperOverlayColor",
  "iconColor",
  // fix-ew-c3-paid-lock-cross-segment-price-contamination (MEASURED live
  // web-dev.iyuga.net, bundle main.3d5e0fc5.js, 2026-09-18): the per-segment
  // Content Lock toggle (SegmentContentLock.jsx) writes `is_locked` straight to
  // Redux without touching any OTHER compared key, so this comparator reported
  // "equal" and React.memo skipped the re-render. The SAME data-loss shape the
  // title/photo/wallpaper fix above already covers: the D3 pie's own internal
  // chartData (createPie.js) never re-synced with the fresh lock, so the NEXT
  // wedge click emitted its stale pre-lock array back into Redux, silently
  // reverting the lock on the segment that was never clicked.
  "is_locked",
  // fix-hw0917-add-to-segment-switch-away-wipes-target-segment (MEASURED live
  // web-dev.iyuga.net, bundle main.5bfcc2cc.js, wheel 6491, 2026-09-18): adding
  // a Capture to the OPEN segment via the "Add to Segment" picker
  // (CaptureSegmentDropProvider's `flushCurrentCanvasNow`) writes the
  // segment's `nodes` straight to Redux without touching any OTHER compared
  // key — same shape as the title/wallpaper/is_locked losses above. The D3
  // pie's own internal chartData never re-synced with the fresh block, so the
  // NEXT wedge click (switching to a different segment) emitted its stale
  // pre-add array back into Redux via `handleSetChartData`, silently
  // reverting the just-added block on the segment that was never clicked (and
  // separately letting the pie's stale copy of the INCOMING segment's own
  // content ride along too, so that segment's own next save replaced rather
  // than appended). `nodes` is a JSON string, so this is the same O(1)-ish
  // string compare already used for `title`/`bgWallpaper` above.
  "nodes",
];

// fix-ew-c3-paid-lock-cross-segment-price-contamination: `lock_type` is a
// nested object (`{ payment_lock: { is_active, amount } }` / password / email
// shape) — a raw `!==` in the scalar loop above would always be "not equal"
// (fresh object identity every render) and force a re-render on every
// unrelated parent update, defeating the memo. Same idiom as `fillSignature`
// above: serialize and compare content, not reference.
const lockTypeSignature = (item) =>
  item?.lock_type ? JSON.stringify(item.lock_type) : "";

// T3.8 (stgfix-1009): the Segment Wallpaper accordion now writes its OWN
// independent `settings.wallpaper` blob (stgfix-1007-P0, PR#6604/#3722) instead
// of the legacy top-level `currentBg/bgWallpaper/segmentBgColor/isPattern/
// patternId/wallpaperOverlayColor` scalars already listed in
// WHEEL_CHART_ITEM_COMPARE_KEYS above. Those legacy keys are no longer written
// by a Wallpaper edit, so they never change and this comparator reported
// "equal" on every image/pattern wallpaper pick -> React.memo skipped
// re-rendering WheelComponent -> the D3 pie's own internal chartData
// (createPie.js) never re-synced with the fresh wallpaper -> the NEXT
// wedge-click flush (handleSetChartData -> setWheelSegments, a FULL ARRAY
// REPLACE) emitted the pie's STALE pre-edit snapshot back into redux, silently
// reverting the just-saved wallpaper (MEASURED live web-stg.iyuga.net
// 2026-10-09, bundle main.ecd01286.js, wheel 507106: a picked SVG pattern
// showed "All changes saved" but GET /api/v2/wheel/507106/ afterwards showed
// the edited segment's wallpaper.current_bg back at 0 and the pattern's CSS
// value instead landed on a DIFFERENT, untouched sibling segment -- the exact
// same stale-D3-snapshot-flush shape this file's `nodes`/`is_locked` entries
// above already document). Same idiom as `fillSignature`/`lockTypeSignature`:
// serialize and compare the nested blob's CONTENT, not object identity (a
// fresh `{...wallpaper}` reference every render would otherwise force a
// re-render on every unrelated parent update). Flag-gated: wallpaper only
// ever carries real data once SEGMENT_WALLPAPER_ENABLED is on (see
// components/SegmentView/segmentWallpaperFlag.js); OFF keeps this comparator
// byte-identical to before this fix.
const wallpaperSignature = (item) => {
  const wallpaper = item?.settings?.wallpaper || item?.wallpaper;
  return wallpaper ? JSON.stringify(wallpaper) : "";
};

// T22.90 (Kelly 2026-10-10 video, web-stg): changing a segment's icon in
// Segment Settings saved, but the wheel kept drawing the OLD icon until some
// unrelated edit re-rendered it. The resolved `icon` is a React element (fresh
// identity every render, so it is deliberately NOT in the scalar keys above);
// compare the icon component's NAME instead -- a stable string that changes
// exactly when the drawn glyph would.
export const iconSignature = (item) => {
  const icon = item?.icon;
  if (!icon) return "";
  if (typeof icon === "string") return icon;
  const type = icon.type;
  return (type && (type.displayName || type.name)) || "icon";
};

/**
 * @param {object} prevProps previous <WheelComponent> props
 * @param {object} nextProps next <WheelComponent> props
 * @returns {boolean} true when the props are equal (skip re-render)
 */
export const wheelPropsEqual = (prevProps, nextProps) => {
  // Don't use JSON.stringify for performance reasons - it's slow for large objects
  // Instead, do targeted comparisons on critical properties

  // Compare userData (name included so the alt text tracks the wheel name)
  if (
    prevProps.userData?.id !== nextProps.userData?.id ||
    prevProps.userData?.url !== nextProps.userData?.url ||
    prevProps.userData?.name !== nextProps.userData?.name
  ) {
    return false;
  }

  // WL-056b: re-render when the video center-media changes. When both are
  // undefined (the default / flag-OFF path) every comparison is false, so this
  // block is a no-op and the memo behaviour is byte-identical to today.
  if (
    prevProps.centerMedia?.type !== nextProps.centerMedia?.type ||
    prevProps.centerMedia?.source !== nextProps.centerMedia?.source ||
    prevProps.centerMedia?.poster !== nextProps.centerMedia?.poster
  ) {
    return false;
  }

  // Compare chart data length first (quick check)
  if (
    !prevProps.chartData ||
    !nextProps.chartData ||
    prevProps.chartData.length !== nextProps.chartData.length
  ) {
    return false;
  }

  // Then compare chart data items more efficiently
  for (let i = 0; i < prevProps.chartData.length; i++) {
    const prevItem = prevProps.chartData[i];
    const nextItem = nextProps.chartData[i];

    // ISP-9999: compare the identity + content scalar keys. Any change (title
    // edit, photo/wallpaper delete, pattern/fill scalar) forces a re-render so
    // the pie re-syncs and cannot later emit a stale array back into redux.
    for (let k = 0; k < WHEEL_CHART_ITEM_COMPARE_KEYS.length; k++) {
      const key = WHEEL_CHART_ITEM_COMPARE_KEYS[k];
      if (prevItem?.[key] !== nextItem?.[key]) {
        return false;
      }
    }

    // EW-003 wedge-fill live update: re-render when the Segment Fill MODEL
    // (Pattern / Gradient / Image / GIF) changes even though `color` did not.
    // Flag OFF → the wedge never paints a fill model, so this compare is skipped
    // (byte-identical to the pre-fix comparator).
    if (
      SEGMENT_ARC_FILLS_ENABLED &&
      fillSignature(prevItem) !== fillSignature(nextItem)
    ) {
      return false;
    }

    // fix-ew-c3-paid-lock-cross-segment-price-contamination: re-render when the
    // segment content-lock's flavor/price/access-code/emails change, even
    // though `is_locked` itself (compared above) did not — e.g. switching the
    // flavor picker from Password to Paid, or editing an already-paid
    // segment's price. Not flag-gated: SegmentContentLock is unconditionally
    // reachable once SEGMENT_LOCK_ENABLED is on, and `lock_type` is absent
    // (both signatures "") on every wheel that never uses the feature, so this
    // is a no-op comparison for those.
    if (lockTypeSignature(prevItem) !== lockTypeSignature(nextItem)) {
      return false;
    }

    // T22.90: a picked / changed / removed / hidden segment icon must redraw.
    if (iconSignature(prevItem) !== iconSignature(nextItem)) {
      return false;
    }

    // T3.8: re-render when the Segment Wallpaper blob (settings.wallpaper)
    // changes, even though the legacy top-level currentBg/bgWallpaper/etc.
    // keys compared above did not (they are no longer written by a Wallpaper
    // edit post stgfix-1007-P0). Flag OFF -> every item's signature is "" ->
    // no-op, byte-identical to before this fix.
    if (
      SEGMENT_WALLPAPER_ENABLED &&
      wallpaperSignature(prevItem) !== wallpaperSignature(nextItem)
    ) {
      return false;
    }
  }

  // fix-editwheel-wheel-stays-small-after-pane-close (Gordon, MEASURED live on
  // web-dev.iyuga.net, main.5cfe6950.js, builder/5504 + builder/5525): the
  // comparator never compared `width`/`containerDivStyle` — the BUILDER_LAYOUT_FIT
  // sizing props (SegmentBuilder/index.jsx `wheelFitPx`) that the canvas pane's
  // ResizeObserver recomputes on every pane open/close and window resize. A
  // ResizeObserver-driven size change alone touches no OTHER compared prop
  // (chartData/userData/etc. all stay identical), so this comparator reported
  // "equal" and React.memo skipped the re-render entirely — freezing
  // WheelComponent's container DOM node (and by extension its OWN internal
  // ResizeObserver + createPie `resize()`/`setSvg()`, which only fire off that
  // node's real size) at whatever size last accompanied a covered-prop change.
  // MEASURED: closing the Wheel Settings pane grew the fit area 516px->786px
  // (confirmed via a ResizeObserver instrumentation log showing the callback
  // firing with the full new width) but the rendered wheel <svg> stayed frozen
  // at 500px indefinitely — it only ever "looked" like open/close worked
  // whenever the same click happened to also change a covered prop (e.g.
  // selecting/deselecting a segment touches `chartData[].selected`).
  if (
    prevProps.width !== nextProps.width ||
    prevProps.containerDivStyle?.height !== nextProps.containerDivStyle?.height ||
    prevProps.containerDivStyle?.marginTop !== nextProps.containerDivStyle?.marginTop
  ) {
    return false;
  }

  // T22.90 (stgfix-10101337, MEASURED live web-stg.iyuga.net, bundle
  // main.a491d0fd.js, wheel 507696): the comparator never looked at `config`,
  // so flipping Wheel > Design > Segments > "Segment Icons" (which changes ONLY
  // config.segmentIconConfig.showOnSegment via SegmentBuilder's
  // builderWheelConfig) compared "equal" and React.memo skipped the re-render.
  // WheelComponent's showOnSegment sync never ran, so clicking Icons painted
  // nothing and the icons only appeared on the NEXT unrelated re-render --
  // e.g. right after clicking None -- making the toggle look inverted. Compare
  // the scalar, not the config object (callers build it inline, so its
  // identity changes every render and would defeat the memo).
  if (
    prevProps.config?.segmentIconConfig?.showOnSegment !==
    nextProps.config?.segmentIconConfig?.showOnSegment
  ) {
    return false;
  }

  // Compare the remaining scalar props
  return (
    prevProps.maxSelectedArcs === nextProps.maxSelectedArcs &&
    prevProps.showLabels === nextProps.showLabels &&
    prevProps.arcThickness === nextProps.arcThickness &&
    prevProps.margin === nextProps.margin &&
    prevProps.segmentHighlighting === nextProps.segmentHighlighting &&
    // ISP-9999 Discover live-ecosystem: `ambientActive` flips true/false on a
    // TIMER, on an otherwise-unchanged card (chartData/userData/etc. are all
    // still equal) — without this line the memo reports "equal" on every
    // ambient tick and WheelComponent never re-renders, so the ambient prop
    // change silently never reaches the wedge-hover-motion effect at all.
    prevProps.ambientActive === nextProps.ambientActive &&
    // stgfix-1008-B43: the Builder flips this true the moment its canvas pane
    // is first measured (wheelFitActive), with no OTHER compared prop
    // necessarily changing on that same render — without this line the memo
    // could skip that render and the new sync-resize useLayoutEffect would
    // never arm.
    prevProps.syncResizeOnPropChange === nextProps.syncResizeOnPropChange
  );
};

// ISP-9999 alias: PR #4525's test + call sites reference the comparator as
// `wheelComponentPropsAreEqual`. Same function, one source of truth (no
// duplicated comparator) — keeps both names resolvable after the EW-003
// extraction to this file.
export const wheelComponentPropsAreEqual = wheelPropsEqual;

export default wheelPropsEqual;
